import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Videocam from '@mui/icons-material/Videocam';
import { Box, ButtonBase, Skeleton, Typography } from '@mui/material';

import CloudflareResizedImage from 'components/CloudflareResizedImage';
import type { CamForecastComparison, CamForecastTopMatch } from 'types/camera';

import { CamMatchesExpanded } from './CamMatchesExpanded';
import { formatCamMatchPeriod } from './formatCamMatchPeriod';
import {
  CAM_MATCH_GRID_THUMB_HEIGHT,
  CAM_MATCH_GRID_THUMB_QUALITY,
  CAM_MATCH_GRID_THUMB_WIDTH,
  CAM_MATCHES_GRID_OUTER_WIDTH,
  getCamMatchGridPrimaryUrl,
} from './prefetchCamMatchThumbnails';
import styles from './CamMatches.module.scss';

const GRID_SLOTS = 4;
const CAM_MATCHES_BODY_ID = 'cam-matches-body';

const CAM_MATCHES_DESCRIPTION =
  'View clips & stills of past conditions that are a close match to the selected forecast';

export { CAM_MATCHES_GRID_OUTER_WIDTH };

export type CamMatchesForecastComparisonMeta = Pick<
  CamForecastComparison,
  'datetime' | 'utcOffset'
>;

export interface CamMatchesProps {
  spotId: string;
  loading?: boolean;
  error?: boolean;
  matches?: readonly CamForecastTopMatch[];
  forecastComparison?: CamMatchesForecastComparisonMeta | null;
  contentLeft?: number;
  contentWidth?: number;
  isExpanded: boolean;
  onToggleExpanded: () => void;
}

export const CamMatches: React.FC<CamMatchesProps> = ({
  spotId,
  loading = false,
  error = false,
  matches,
  forecastComparison = null,
  contentLeft,
  contentWidth,
  isExpanded: isSectionExpanded,
  onToggleExpanded: toggleSection,
}) => {
  const showNoConditions = !loading && matches !== undefined && matches.length === 0;
  const displayMatches = matches?.slice(0, GRID_SLOTS) ?? [];
  const showGrid =
    !loading && !showNoConditions && matches !== undefined && displayMatches.length > 0;

  const [expandedMatch, setExpandedMatch] = useState<CamForecastTopMatch | null>(null);

  const comparisonDatetime = forecastComparison?.datetime ?? null;
  const previousDatetimeRef = useRef(comparisonDatetime);
  useEffect(() => {
    const previous = previousDatetimeRef.current;
    previousDatetimeRef.current = comparisonDatetime;
    if (comparisonDatetime == null) return;
    if (previous === comparisonDatetime) return;
    setExpandedMatch((current) => (current === null ? null : (matches?.[0] ?? null)));
  }, [comparisonDatetime, matches]);

  const showBody = loading || error || matches !== undefined;

  const handleCellClick = (match: CamForecastTopMatch) => {
    setExpandedMatch(match);
  };

  const gridEl =
    showGrid && !expandedMatch ? (
      <Box className={styles.card}>
        <Box className={styles.grid} data-testid="cam-matches-grid">
          {Array.from({ length: GRID_SLOTS }, (_, index) => {
            const match = displayMatches[index];
            const src = getCamMatchGridPrimaryUrl(match);
            const key = match?.exemplarId ?? `slot-${index}`;
            const period = match ? formatCamMatchPeriod(match.localTimestamp) : '';

            return (
              <ButtonBase
                key={key}
                component={match ? 'button' : 'div'}
                type={match ? 'button' : undefined}
                className={classNames(
                  styles.cell,
                  match && styles.cellInteractive,
                  !src && styles.cellEmpty,
                )}
                data-testid={`cam-matches-cell-${index}`}
                disabled={!match}
                onClick={match ? () => handleCellClick(match) : undefined}
              >
                {match?.overallSimilarity != null && (
                  <Box className={styles.matchLabel}>
                    <Typography component="span" className={styles.matchPct}>
                      {Math.round(match.overallSimilarity)}%
                    </Typography>
                    <Typography component="span" className={styles.matchRest}>
                      {period ? ` match · ${period}` : ' match'}
                    </Typography>
                  </Box>
                )}
                {src ? (
                  <>
                    <Box className={styles.thumbWrap}>
                      <CloudflareResizedImage
                        src={src}
                        alt={
                          match?.localTimestamp
                            ? `Cam match ${match.localTimestamp}`
                            : 'Cam match thumbnail'
                        }
                        width={CAM_MATCH_GRID_THUMB_WIDTH}
                        height={CAM_MATCH_GRID_THUMB_HEIGHT}
                        sizes={`${CAM_MATCH_GRID_THUMB_WIDTH}px`}
                        layout="fill"
                        objectFit="cover"
                        objectPosition="center"
                        loading="eager"
                        priority
                        quality={CAM_MATCH_GRID_THUMB_QUALITY}
                        cloudflareOptions={{ fit: 'cover' }}
                      />
                    </Box>
                    <Box aria-hidden className={styles.thumbGradient} />
                    {match?.highlight ? (
                      <Box
                        className={styles.videoBadge}
                        data-testid={`cam-matches-video-badge-${index}`}
                      >
                        <Videocam className={styles.videoBadgeIcon} aria-hidden />
                        <Typography component="span" className={styles.videoBadgeLabel}>
                          Video
                        </Typography>
                      </Box>
                    ) : null}
                  </>
                ) : (
                  <Box className={styles.placeholder}>
                    <Typography
                      component="p"
                      variant="caption1"
                      data-testid={`cam-matches-placeholder-${index}`}
                    >
                      No match available
                    </Typography>
                  </Box>
                )}
              </ButtonBase>
            );
          })}
        </Box>
      </Box>
    ) : null;

  const loadingGridEl = (
    <Box className={styles.card}>
      <Box className={styles.grid} data-testid="cam-matches-grid">
        {Array.from({ length: GRID_SLOTS }, (_, index) => (
          <Box key={`loading-${index}`} className={styles.cell}>
            <Box className={styles.thumbWrap}>
              <Skeleton
                variant="rectangular"
                width="100%"
                height="100%"
                animation="wave"
                aria-hidden
                data-testid="cam-matches-loading-skeleton-cell"
                sx={{ borderRadius: '4px' }}
              />
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );

  const emptyGridEl = (
    <Box className={styles.card}>
      <Box
        className={styles.emptyState}
        data-testid="cam-matches-empty"
        role="status"
        aria-live="polite"
      >
        <Typography component="p" variant="caption1">
          No matching conditions
        </Typography>
      </Box>
    </Box>
  );

  const errorGridEl = (
    <Box className={styles.card}>
      <Box className={styles.errorState} data-testid="cam-matches-error" role="alert">
        <Typography component="p" variant="caption1">
          Cam Matches are temporarily unavailable
        </Typography>
      </Box>
    </Box>
  );

  const bodyPositionStyle =
    contentLeft != null || contentWidth != null
      ? {
          marginLeft: contentLeft ?? 0,
          ...(contentWidth != null ? { maxWidth: contentWidth, width: contentWidth } : {}),
        }
      : undefined;

  const bodyContent = loading ? (
    <Box data-testid="cam-matches-skeleton">{loadingGridEl}</Box>
  ) : error ? (
    errorGridEl
  ) : showNoConditions ? (
    emptyGridEl
  ) : expandedMatch ? (
    <CamMatchesExpanded
      spotId={spotId}
      match={expandedMatch}
      forecastComparison={forecastComparison ?? null}
      onCollapse={() => setExpandedMatch(null)}
    />
  ) : (
    gridEl
  );

  return (
    <Box
      className={classNames(
        styles.root,
        styles.section,
        !isSectionExpanded && styles.sectionCollapsed,
      )}
      data-testid="cam-matches"
      data-expanded={isSectionExpanded ? 'true' : 'false'}
    >
      <ButtonBase
        component="button"
        type="button"
        className={styles.headerButton}
        aria-expanded={isSectionExpanded}
        aria-controls={CAM_MATCHES_BODY_ID}
        data-testid="cam-matches-section-toggle"
        disableRipple
        disableTouchRipple
        onClick={toggleSection}
      >
        <Box className={styles.headerLeading}>
          <ExpandMoreIcon
            className={classNames(styles.chevron, isSectionExpanded && styles.chevronExpanded)}
            aria-hidden
          />
          <Typography component="span" className={styles.headerTitle}>
            Cam Matches
          </Typography>
        </Box>
        <Typography component="span" className={styles.headerDescription}>
          {CAM_MATCHES_DESCRIPTION}
        </Typography>
      </ButtonBase>

      {isSectionExpanded && showBody ? (
        <Box
          id={CAM_MATCHES_BODY_ID}
          className={styles.body}
          style={bodyPositionStyle}
          aria-busy={loading}
          data-testid="cam-matches-body"
        >
          {bodyContent}
        </Box>
      ) : null}
    </Box>
  );
};
